import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from '@mui/material';

const conversationBands = [
  'Up to 200',
  '200 - 999',
  '1000 - 4 999',
  '5 000 - 9 999',
  '10 000 or more',
  "I don't know",
];

export default function OnboardingPreferences() {
  const navigate = useNavigate();
  const [businessModel, setBusinessModel] = useState('');
  const [businessModelOther, setBusinessModelOther] = useState('');
  const [industry, setIndustry] = useState('');
  const [conversations, setConversations] = useState('');
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      try {
        localStorage.setItem('onboarding_business_model', businessModel);
        localStorage.setItem('onboarding_business_model_other', businessModelOther);
        localStorage.setItem('onboarding_industry', industry);
        localStorage.setItem('onboarding_conversations', conversations);
      } catch (_) {}

      // Persist to DB (idempotent upsert)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (userId) {
          await supabase
            .from('user_onboarding')
            .upsert({
              user_id: userId,
              business_model: businessModel || null,
              business_model_other: businessModelOther || null,
              industry: industry || null,
              conversations_band: conversations || null,
            }, { onConflict: 'user_id' });
        }
      } catch (e) {
        // Non-blocking
        console.warn('[OnboardingPreferences] Failed to persist onboarding answers:', e);
      }
    } finally {
      setSaving(false);
      navigate('/onboarding/profile-3');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={4}>
              <div>
                <Typography variant="h6" gutterBottom>What is your primary business model?</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  <Button
                    variant={businessModel === 'services' ? 'contained' : 'outlined'}
                    onClick={() => setBusinessModel('services')}
                    sx={{ minWidth: 280, justifyContent: 'flex-start', p: 2, borderRadius: 2 }}
                  >
                    I provide services (to businesses or consumers)
                  </Button>
                  <Button
                    variant={businessModel === 'ecommerce' ? 'contained' : 'outlined'}
                    onClick={() => setBusinessModel('ecommerce')}
                    sx={{ minWidth: 280, justifyContent: 'flex-start', p: 2, borderRadius: 2 }}
                  >
                    E-commerce (online store)
                  </Button>
                  <Button
                    variant={businessModel === 'other' ? 'contained' : 'outlined'}
                    onClick={() => setBusinessModel('other')}
                    sx={{ minWidth: 140, p: 2, borderRadius: 2 }}
                  >
                    Other
                  </Button>
                </Stack>
                {businessModel === 'other' && (
                  <Box sx={{ mt: 2, maxWidth: 300 }}>
                    <TextField
                      fullWidth
                      placeholder="restaurant"
                      value={businessModelOther}
                      onChange={(e) => setBusinessModelOther(e.target.value)}
                    />
                  </Box>
                )}
              </div>

              <div>
                <Typography variant="h6" gutterBottom>What is your company’s industry?</Typography>
                <FormControl fullWidth>
                  <InputLabel id="industry-label">Industry</InputLabel>
                  <Select
                    labelId="industry-label"
                    label="Industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                  >
                    <MenuItem value="Food and Beverage">Food and Beverage</MenuItem>
                    <MenuItem value="Retail">Retail</MenuItem>
                    <MenuItem value="Software">Software</MenuItem>
                    <MenuItem value="Healthcare">Healthcare</MenuItem>
                    <MenuItem value="Education">Education</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div>
                <Typography variant="h6" gutterBottom>How many customer conversations does your company handle per month?</Typography>
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  {conversationBands.map((band) => (
                    <Button
                      key={band}
                      variant={conversations === band ? 'contained' : 'outlined'}
                      onClick={() => setConversations(band)}
                      sx={{ borderRadius: 2 }}
                    >
                      {band}
                    </Button>
                  ))}
                </Stack>
              </div>

              <Divider />

              <Stack direction="row" spacing={2}>
                <Button onClick={() => navigate('/onboarding/profile')}>Back</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="contained" onClick={handleContinue} disabled={saving}>Continue</Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 2, height: { xs: 320, md: 560 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 520 }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1200&auto=format&fit=crop"
                alt="Team working"
                sx={{ width: '100%', borderRadius: 2, display: 'block' }}
              />
              <Stack spacing={2} sx={{ position: 'absolute', left: -20, top: 40 }}>
                <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>🛒</Avatar>
                <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>🏷️</Avatar>
                <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>🧰</Avatar>
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}


